import { useEffect, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { CouponView } from "@/lib/api/types";
import { SpotlightCard } from "@/shared/components/SpotlightCard";
import {
  couponMinOrderText,
  couponTypeBadge,
  couponValueText,
} from "@/modules/coupons/lib/coupon-display";
import {
  discountScheduleText,
  discountStatusTone,
} from "@/modules/coupons/lib/discount-display";

type DiscountCardProps = {
  locale: Locale;
  discount: CouponView;
  isMutating: boolean;
  onEdit: (discount: CouponView) => void;
  onDelete: (discount: CouponView) => void;
  /** Extra classes on the card root (e.g. grid col-span for the odd last card). */
  className?: string;
};

/**
 * Automatic-discount card: type chip + status, big value, min-order, an
 * "applies automatically" strip (no code to copy/share), combinable badge,
 * schedule line, and a ⋮ menu (edit / delete).
 */
export function DiscountCard({
  locale,
  discount,
  isMutating,
  onEdit,
  onDelete,
  className,
}: DiscountCardProps) {
  const t = useTranslations();
  const [menuOpen, setMenuOpen] = useState(false);
  const tone = discountStatusTone(discount, t);
  const typeBadge = couponTypeBadge(discount, t);
  const value = couponValueText(discount, locale, t);
  const minOrderText = couponMinOrderText(discount, locale, t);
  const scheduleText = discountScheduleText(discount, locale, t);

  return (
    <SpotlightCard
      glow={`color-mix(in oklab, ${typeBadge.fg} 10%, transparent)`}
      className={[
        "flex flex-col rounded-[18px] border border-[color:var(--card-br)] bg-[color:var(--surface)] shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      suppressTooltip={menuOpen}
      tooltip={
        <>
          <div className="mb-2 flex items-center justify-between gap-5 text-[11.5px]">
            <span className="text-[color:var(--text-muted)]">
              {t(keys.discounts.tableDiscount)}
            </span>
            <span className="font-bold" style={{ color: typeBadge.fg }}>
              <bdi>{value}</bdi>
            </span>
          </div>
          <div className="flex items-center justify-between gap-5 text-[11.5px]">
            <span className="text-[color:var(--text-muted)]">
              {t(keys.discounts.timesApplied)}
            </span>
            <span className="font-semibold text-[color:var(--text-primary)]">
              <bdi dir="ltr">{discount.usesCount}</bdi>
            </span>
          </div>
          {minOrderText ? (
            <div className="mt-1.5 flex items-center justify-between gap-5 text-[11.5px]">
              <span className="text-[color:var(--text-muted)]">
                {t(keys.discounts.minOrderLabel)}
              </span>
              <span className="font-semibold text-[color:var(--text-primary)]">
                <bdi>{minOrderText}</bdi>
              </span>
            </div>
          ) : null}
        </>
      }
    >
      {/* Top */}
      <div className="px-[17px] pb-[14px] pt-[15px]">
        <div className="mb-[13px] flex items-center justify-between gap-2">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold"
            style={{ background: typeBadge.bg, color: typeBadge.fg }}
          >
            {typeBadge.label}
          </span>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-[9px] py-1 text-[11px] font-semibold"
            style={{ background: tone.bg, color: tone.fg }}
          >
            <span
              aria-hidden="true"
              className="h-[5px] w-[5px] rounded-full"
              style={{ background: tone.dot }}
            />
            {tone.label}
          </span>
        </div>
        <div className="text-[27px] font-extrabold leading-[1.05] tracking-[-0.02em] text-[color:var(--text-primary)]">
          <bdi>{value}</bdi>
        </div>
        <div className="mt-[7px] text-[12px] text-[color:var(--text-muted)]">
          <bdi>{minOrderText ?? t(keys.discounts.noMinimum)}</bdi>
        </div>
      </div>

      {/* Perforated notch */}
      <div className="relative h-0">
        <div className="absolute -start-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[color:var(--canvas)]" />
        <div className="absolute -end-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[color:var(--canvas)]" />
        <div
          className="absolute -top-px start-[9px] end-[9px] h-0.5"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg,var(--border-strong) 0 2px,transparent 2px 8px)",
          }}
        />
      </div>

      {/* Bottom */}
      <div className="rounded-b-[18px] bg-[color:var(--background-muted)] px-[17px] pb-4 pt-[15px]">
        {/* Auto-applies strip (replaces the coupon code row) */}
        <div className="flex items-center gap-2 rounded-[10px] border border-dashed border-[color:var(--border-strong)] bg-[color:var(--surface)] px-3 py-2.5">
          <BoltIcon />
          <span className="truncate text-[12.5px] font-semibold text-[color:var(--text-secondary)]">
            {t(keys.discounts.autoApplies)}
          </span>
        </div>

        {/* Combinable + applied count */}
        <div className="mt-3.5 flex items-center justify-between gap-2 text-[11.5px] font-semibold">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-1"
            style={
              discount.combinable
                ? {
                    background: "color-mix(in oklab, #16A34A 12%, transparent)",
                    color: "#15803D",
                  }
                : {
                    background: "var(--surface-alt)",
                    color: "var(--text-muted)",
                  }
            }
          >
            <LayersIcon />
            {discount.combinable
              ? t(keys.discounts.combinableYes)
              : t(keys.discounts.combinableNo)}
          </span>
          <span className="text-[color:var(--text-muted)]">
            {t(keys.discounts.appliedCount, { count: discount.usesCount })}
          </span>
        </div>

        {/* Schedule + menu */}
        <div className="mt-3.5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--text-muted)]">
            <ClockIcon />
            {scheduleText}
          </span>
          <Menu as="div" className="relative">
            {({ open }) => (
              <>
                <MenuOpenSync open={open} onChange={setMenuOpen} />
                <MenuButton
                  aria-label={t(keys.common.actions)}
                  disabled={isMutating}
                  className="flex h-10 w-10 items-center justify-center rounded-[9px] border border-[color:var(--card-br)] bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover)] disabled:opacity-50 lg:h-8 lg:w-8"
                >
                  <DotsIcon />
                </MenuButton>
                <MenuItems
                  anchor="bottom end"
                  className="z-50 mt-1.5 w-[160px] overflow-hidden rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-1.5 shadow-[0_12px_32px_rgba(15,20,40,0.14)] focus:outline-none"
                >
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => onEdit(discount)}
                      className="flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-start text-[13px] font-medium text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--surface-alt)]"
                    >
                      <EditIcon />
                      {t(keys.common.edit)}
                    </button>
                  </MenuItem>
                  <div
                    aria-hidden="true"
                    className="my-1 h-px bg-[color:var(--border-light)]"
                  />
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => onDelete(discount)}
                      className="flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-start text-[13px] font-semibold text-[color:var(--error-500)] data-[focus]:bg-[color:var(--error-500)]/10"
                    >
                      <TrashIcon />
                      {t(keys.common.delete)}
                    </button>
                  </MenuItem>
                </MenuItems>
              </>
            )}
          </Menu>
        </div>
      </div>
    </SpotlightCard>
  );
}

/**
 * Bridges the Headless UI Menu's render-prop `open` state up to the card so it
 * can suppress the SpotlightCard tooltip while the dropdown is open.
 */
function MenuOpenSync({
  open,
  onChange,
}: {
  open: boolean;
  onChange: (open: boolean) => void;
}) {
  useEffect(() => {
    onChange(open);
  }, [open, onChange]);
  return null;
}

/* ── Icons ─────────────────────────────────────────────────────────── */

const S = {
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function BoltIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      {...S}
      className="shrink-0 text-[color:var(--accent-600)]"
    >
      <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z" />
    </svg>
  );
}
function LayersIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
      <path d="m12 2 9 5-9 5-9-5 9-5Z" />
      <path d="m3 12 9 5 9-5" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}
